import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import DataTable, { createDataTableSelectColumn } from 'src/components/DataTable';
import { DataTableNotFoundMode, IDataTableRef } from 'src/components/DataTable/shared/types';
import { translate } from 'src/locales/i18next/config';
import Translations from 'src/locales/i18next/translations/translation';
import IncludedFlashProductsFilter from '../components/IncludedFlashProductsFilter';
import FlashProductCancelConfirmModal from '../components/modals/FlashProductCancelConfirmModal';
import useAcceptedFlashProductsColumns from '../helpers/colum-definitions/use-accepted-flash-products-columns';
import { includedFlashProductsFilterValuesInitialValues } from '../helpers/constants';
import { flashProductsFilterSerializer } from '../helpers/helper';
import flashProductsResponseAdapter from '../services/adapter/flash-products-response-adapter';
import { getAcceptedFlashProducts, getAcceptedFlashProductsQueryKey } from '../services/api/get-accepted-flash-products';
import useBulkCancelFlashDiscount from '../services/hooks/use-bulk-cancel-flash-discount';
import { FlashProductTransition } from '../services/models/flash-discount';
import type { IAcceptedFlashProductListItem } from '../services/models/flash-discount';
import FlashProductsTabs from '../tabs';

type CancelConfirmSource = 'bulk' | 'single' | null;

export default function FlashProductsIncludedPage() {
  const t = translate(Translations);
  const translation = 'pages.flashProducts';

  const dataTableRef = useRef<IDataTableRef>(null);
  const [selectedCount, setSelectedCount] = useState(0);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [cancelSource, setCancelSource] = useState<CancelConfirmSource>(null);
  const [pendingSingleId, setPendingSingleId] = useState<number | null>(null);
  const [transition, setTransition] = useState<FlashProductTransition | null>(includedFlashProductsFilterValuesInitialValues.transition);

  const initialValues = {
    pagination: { pageSize: 50 },
  };

  const closeCancelModal = useCallback(() => {
    setShowCancelConfirmModal(false);
    setCancelSource(null);
    setPendingSingleId(null);
  }, []);

  const bulkCancelMutation = useBulkCancelFlashDiscount({
    onSuccess: () => {
      dataTableRef.current?.invalidateDataTableQuery?.();
      dataTableRef.current?.clearSelection();
      setSelectedCount(0);
      closeCancelModal();
      toast.success('Seçilen flaş indirimleri iptal edildi.');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.message || 'İptal işlemi sırasında bir hata oluştu.');
    },
  });

  const baseColumns = useAcceptedFlashProductsColumns({
    transition,
    dataTableRef,
    onRequestRowCancel: useCallback((id: number) => {
      setPendingSingleId(id);
      setCancelSource('single');
      setShowCancelConfirmModal(true);
    }, []),
  });
  const showSelection = !!transition && ![FlashProductTransition.finished, FlashProductTransition.canceled].includes(transition);

  const columns = useMemo(() => (showSelection ? [createDataTableSelectColumn(), ...baseColumns] : baseColumns), [baseColumns, showSelection]);

  const handleTransitionChange = useMemo(
    () => (value: FlashProductTransition | null) => {
      setTransition(value);
    },
    [],
  );

  const handleRowSelectionChange = useMemo(
    () => (rowSelection: Record<string, boolean>) => {
      setSelectedCount(Object.values(rowSelection).filter(Boolean).length);
    },
    [],
  );

  const handleCancelSelectionClick = () => {
    setCancelSource('bulk');
    setShowCancelConfirmModal(true);
  };

  const handleConfirmCancel = () => {
    if (cancelSource === 'single' && pendingSingleId != null) {
      bulkCancelMutation.mutate({ flashDiscountIds: [pendingSingleId] });
      return;
    }
    const selectedRows = dataTableRef.current?.getSelectedRows<IAcceptedFlashProductListItem>() ?? [];
    const flashDiscountIds = selectedRows.map((row) => row.periodInfo?.[0]?.id).filter((id) => id != null);
    if (flashDiscountIds.length === 0) return;
    bulkCancelMutation.mutate({ flashDiscountIds });
  };

  const handleCloseCancelConfirmModal = () => {
    if (!bulkCancelMutation.isLoading) closeCancelModal();
  };

  const isCancelLoading = bulkCancelMutation.isLoading;

  useEffect(() => {
    if (transition && ![FlashProductTransition.finished, FlashProductTransition.canceled].includes(transition)) {
      dataTableRef.current?.clearSelection();
      setSelectedCount(0);
    }
  }, [transition]);

  if (!baseColumns.length) return null;
  return (
    <>
      <div>
        <h3 className="mb-7">{t(`${translation}.flashProducts`)}</h3>

        <div className="card card-custom mb-4">
          <FlashProductsTabs />

          <div className="card-body">
            <div className="tab-content pt-3">
              <div className="tab-pane active">
                <IncludedFlashProductsFilter dataTableRef={dataTableRef} onTransitionChange={handleTransitionChange} />

                {showSelection && (
                  <div className="mb-3 mt-3">
                    <button type="button" className="btn btn-light-danger btn-sm" onClick={handleCancelSelectionClick} disabled={!selectedCount}>
                      Seçilenleri İptal Et {selectedCount > 0 && `(${selectedCount})`}
                    </button>
                  </div>
                )}

                <FlashProductCancelConfirmModal
                  show={showCancelConfirmModal}
                  onHide={handleCloseCancelConfirmModal}
                  onConfirm={handleConfirmCancel}
                  isLoading={isCancelLoading}
                  message={`Seçilen ${selectedCount > 1 ? 'ürünler' : 'ürün'}e ait flaş indirimleri iptal etmek istediğinize emin misiniz?`}
                />

                <DataTable
                  ref={dataTableRef}
                  columns={columns}
                  queryKey={getAcceptedFlashProductsQueryKey}
                  queryFn={getAcceptedFlashProducts}
                  responseAdapter={flashProductsResponseAdapter}
                  initial={initialValues}
                  filterSerializer={flashProductsFilterSerializer}
                  enableRowSelection={showSelection}
                  onRowSelectionChange={showSelection ? handleRowSelectionChange : undefined}
                  notFoundOptions={{
                    mode: DataTableNotFoundMode.showWithinTable,
                    title: 'Aradığınız özelliklere uygun ürün şu anda listelenmemektedir.',
                    description: '"Kampanya Oluştur" ile kendi kampanyanızı başlatabilirsiniz.',
                    actionBtn: {
                      label: 'Kampanya Oluştur',
                      slug: '/campaigns/create',
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
